/**
 * <AuditTrail /> — browse the admin_events audit log.
 *
 * Shows every high-signal admin action (role changes, admin messages,
 * cancellations, warnings, notes, publish/reject, backup ops…) with actor,
 * target, timestamp, and reason. Filterable by action type + free-text
 * search across comment/meta.
 */
import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ScrollText, Filter, Search, User, Shield } from "lucide-react";

const ACTION_LABEL = {
  role_change: "Role change",
  admin_message: "Message sent",
  warning: "Warning",
  note: "Admin note",
  status_change: "Status change",
  trusted_publisher: "Trusted publisher",
  approve: "Approved",
  reject: "Rejected",
  archive: "Archived",
  unarchive: "Unarchived",
  delete: "Deleted",
  publish: "Published",
  edit: "Edited",
  force_reseed: "Force reseed",
  restore: "Restored backup",
  backup: "Backup created",
};

const ACTION_COLOUR = {
  role_change: "#8b5cf6",
  admin_message: "#0ea5e9",
  warning: "#f59e0b",
  status_change: "#dc2626",
  approve: "#16a34a",
  reject: "#dc2626",
  archive: "#94a3b8",
  restore: "#0ea5e9",
  delete: "#dc2626",
};

export default function AuditTrail() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [entityType, setEntityType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (action) params.action = action;
      if (entityType) params.entity_type = entityType;
      const { data } = await http.get("/admin/audit-events", { params });
      setItems(data.items || data || []);
    } catch (e) {
      toast.error("Couldn't load audit log.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, entityType]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((it) => {
      const blob = JSON.stringify({
        c: it.comment,
        m: it.meta,
        a: it.admin_name,
        e: it.entity_type,
        eid: it.entity_id,
      }).toLowerCase();
      return blob.includes(s);
    });
  }, [items, q]);

  const uniqueActions = useMemo(() => {
    const s = new Set(items.map((i) => i.action).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);
  const uniqueEntities = useMemo(() => {
    const s = new Set(items.map((i) => i.entity_type).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div data-testid="audit-trail" className="wofy-card">
      <h2 className="font-display text-xl mb-3">
        <ScrollText className="inline -mt-1 w-5 h-5 mr-2" />
        Audit log
      </h2>
      <p className="text-xs mb-4" style={{ color: "var(--wofy-text-soft)" }}>
        Every role change, admin message, and moderation action — with actor + timestamp + reason.
      </p>

      <div className="flex flex-wrap gap-2 mb-4 text-sm">
        <div className="relative flex-1 min-w-[220px]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reason / actor / target…"
            className="wofy-input w-full pr-8"
            data-testid="audit-search"
          />
          <Search className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 opacity-50" />
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)} className="wofy-input" data-testid="audit-filter-action">
          <option value="">All actions</option>
          {uniqueActions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>)}
        </select>
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="wofy-input" data-testid="audit-filter-entity">
          <option value="">All entities</option>
          {uniqueEntities.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-center py-8" style={{ color: "var(--wofy-text-soft)" }}>Nothing recorded yet in this window.</p>
      ) : (
        <div className="space-y-2" data-testid="audit-list">
          {filtered.slice(0, 100).map((it, i) => {
            const clr = ACTION_COLOUR[it.action] || "#64748b";
            return (
              <div
                key={it.id || i}
                className="text-sm rounded-lg border p-3"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid={`audit-row-${it.id || i}`}
              >
                <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "var(--wofy-text-soft)" }}>
                  <span className="font-mono">{new Date(it.created_at || it.at).toLocaleString()}</span>
                  <span className="rounded-full font-bold px-2 py-0.5 uppercase tracking-widest text-[10px]" style={{ background: `${clr}22`, color: clr }}>
                    {ACTION_LABEL[it.action] || it.action}
                  </span>
                  <span className="opacity-70">{it.entity_type}</span>
                </div>
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="inline-flex items-center gap-1">
                    <Shield className="w-3 h-3 opacity-50" />
                    <b>{it.admin_name || it.admin_id || "system"}</b>
                  </span>
                  <span className="opacity-60">→</span>
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3 opacity-50" />
                    <span className="font-mono opacity-80 text-xs">{it.entity_id?.slice(0, 8) || "—"}</span>
                  </span>
                  {it.meta?.to && (
                    <span className="text-xs opacity-70">→ role <b>{it.meta.to}</b></span>
                  )}
                  {it.meta?.from && it.meta?.to && (
                    <span className="text-xs opacity-70">(was {it.meta.from})</span>
                  )}
                </div>
                {it.comment && (
                  <div className="mt-1 text-xs italic opacity-80">"{it.comment}"</div>
                )}
                {it.meta && Object.keys(it.meta).filter((k) => !["to", "from"].includes(k)).length > 0 && (
                  <details className="mt-1 text-xs opacity-60">
                    <summary className="cursor-pointer">details</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-[10px] bg-black/5 p-2 rounded">{JSON.stringify(it.meta, null, 2)}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
      {filtered.length > 100 && (
        <p className="mt-3 text-xs text-center" style={{ color: "var(--wofy-text-soft)" }}>
          Showing first 100 · {filtered.length - 100} more match your filters.
        </p>
      )}
    </div>
  );
}
